import PlainDate from '@Domain/PlainDate';
import { theme } from '@Guidelines/Theme/theme';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import type FieldValueFormData from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Sidebars/DocumentPanelSidebar/FieldPanel/FormData/FieldValueFormData';
import type { FieldValueDraft } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/Sidebars/DocumentPanelSidebar/FieldPanel/FormData/FieldValueFormData';
import { formatLongCalendarDate } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Documents/DocumentDetails/SectionTabs/SectionPanel/DocumentEntry/FieldCard/Helpers/dateFormatter';
import type DocumentFieldView from '@UI/Store/Documents/Views/DocumentFieldView';
import { css } from '@emotion/react';
import * as React from 'react';
import {
	Calendar,
	Checkbox,
	Dropdown,
	InputNumber,
	InputText,
	RadioButton,
	SelectButton,
	TextArea,
} from '@verclam/ui-kit';

const MAX_RADIO_OPTIONS = 7;

const BOOLEAN_OPTIONS = [
	{ value: true, label: 'Oui' },
	{ value: false, label: 'Non' },
];

const optionListStyle = css`
	margin: 0;
	padding: 0;
	border: none;
	display: flex;
	flex-direction: column;
	gap: 0.375rem;
`;

const optionRowStyle = css`
	display: flex;
	align-items: center;
	gap: 0.75rem;
	padding: 0.6875rem 0.875rem;
	border-radius: ${theme.radii.medium};
	background-color: ${theme.colors.surfaceMuted};
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.colors.ink};
	cursor: pointer;
`;

const printedDateStyle = css`
	margin: 0.375rem 0 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkMuted};
`;

type Props = {
	readonly field: DocumentFieldView;
	readonly formData: FieldValueFormData;
	/** A long text is edited in a growing text area rather than on one line. */
	readonly isLongText: boolean;
	readonly isDisabled: boolean;
	readonly onChange: (value: FieldValueDraft) => void;
	/** Wraps the control, so that "Compléter" can move the focus into it. */
	readonly containerRef: React.RefObject<HTMLDivElement | null>;
};

/**
 * The control matching the field's type. It only reads and reports the draft: saving, cancelling and
 * the guards belong to the panel around it.
 */
const FieldValueControl: React.FC<Props> = ({
	field,
	formData,
	isLongText,
	isDisabled,
	onChange,
	containerRef,
}) => {
	const { value } = formData;
	const errorMessage = formData.getValidationMessage('value');
	const isFormFieldValid = formData.isValid('value');
	const options = field.options ?? [];

	const handleTextChange = React.useCallback(
		(next: string) => {
			onChange(next);
		},
		[onChange],
	);

	const handleNumberChange = React.useCallback(
		(next: number | null) => {
			onChange(next === null ? '' : String(next));
		},
		[onChange],
	);

	const handleDateChange = React.useCallback(
		(next: Date | null) => {
			onChange(next === null ? '' : PlainDate.fromCalendar(next).toApi());
		},
		[onChange],
	);

	const handleBooleanChange = React.useCallback(
		(next: boolean | readonly boolean[] | null) => {
			onChange(typeof next === 'boolean' ? next : null);
		},
		[onChange],
	);

	const handleChoiceChange = React.useCallback(
		(next: string | null) => {
			onChange(next);
		},
		[onChange],
	);

	const handleOptionToggle = React.useCallback(
		(isChecked: boolean, optionValue: unknown) => {
			const picked = Array.isArray(value) ? value : [];
			const optionKey = String(optionValue);

			onChange(isChecked ? [...picked, optionKey] : picked.filter((entry) => entry !== optionKey));
		},
		[onChange, value],
	);

	const renderControl = (): React.ReactNode => {
		switch (formData.fieldTypeValue) {
			case 'number':
				return (
					<InputNumber
						name="value"
						label={field.label}
						isLabelHidden
						value={typeof value === 'string' && value !== '' ? Number(value) : null}
						onChange={handleNumberChange}
						errorMessage={errorMessage}
						isFormFieldValid={isFormFieldValid}
						disabled={isDisabled}
					/>
				);
			case 'date': {
				const printable = typeof value === 'string' ? PlainDate.fromApi(value) : null;

				return (
					<React.Fragment>
						<Calendar
							name="value"
							label={field.label}
							isLabelHidden
							dateFormat="dd/mm/yy"
							value={printable === null ? null : printable.toCalendar()}
							onChange={handleDateChange}
							errorMessage={errorMessage}
							isFormFieldValid={isFormFieldValid}
							disabled={isDisabled}
						/>
						<ConditionalLoader data={printable !== null && isFormFieldValid}>
							<p css={printedDateStyle}>
								Imprimé dans le document :{' '}
								{typeof value === 'string' ? formatLongCalendarDate(value) : ''}
							</p>
						</ConditionalLoader>
					</React.Fragment>
				);
			}
			case 'boolean':
				return (
					<SelectButton
						name="value"
						label={field.label}
						isLabelHidden
						options={BOOLEAN_OPTIONS}
						value={typeof value === 'boolean' ? value : null}
						onChange={handleBooleanChange}
						allowEmpty={false}
						disabled={isDisabled}
						errorMessage={errorMessage}
						isFormFieldValid={isFormFieldValid}
					/>
				);
			case 'choice':
				return renderChoice();
			default:
				return isLongText ? (
					<TextArea
						name="value"
						label={field.label}
						isLabelHidden
						autoResize
						rows={4}
						value={typeof value === 'string' ? value : ''}
						onChange={handleTextChange}
						errorMessage={errorMessage}
						isFormFieldValid={isFormFieldValid}
						disabled={isDisabled}
					/>
				) : (
					<InputText
						name="value"
						label={field.label}
						isLabelHidden
						value={typeof value === 'string' ? value : ''}
						onChange={handleTextChange}
						errorMessage={errorMessage}
						isFormFieldValid={isFormFieldValid}
						disabled={isDisabled}
					/>
				);
		}
	};

	const renderChoice = (): React.ReactNode => {
		if (formData.isMultiple) {
			const picked = Array.isArray(value) ? value : [];

			return (
				<fieldset css={optionListStyle} aria-label={field.label}>
					{options.map((option) => (
						<div key={option.value} css={optionRowStyle}>
							<Checkbox
								name={`value-${option.value}`}
								label={option.label}
								checked={picked.includes(option.value)}
								value={option.value}
								onChange={handleOptionToggle}
								disabled={isDisabled}
							/>
						</div>
					))}
				</fieldset>
			);
		}

		if (options.length > MAX_RADIO_OPTIONS) {
			return (
				<Dropdown
					name="value"
					label={field.label}
					isLabelHidden
					options={options}
					optionLabel="label"
					optionValue="value"
					value={typeof value === 'string' ? value : null}
					onChange={handleChoiceChange}
					disabled={isDisabled}
					errorMessage={errorMessage}
					isFormFieldValid={isFormFieldValid}
				/>
			);
		}

		return (
			<fieldset css={optionListStyle} aria-label={field.label}>
				{options.map((option) => (
					<div key={option.value} css={optionRowStyle}>
						<RadioButton
							name="value"
							label={option.label}
							value={option.value}
							checked={value === option.value}
							onChange={handleChoiceChange}
							disabled={isDisabled}
						/>
					</div>
				))}
			</fieldset>
		);
	};

	return <div ref={containerRef}>{renderControl()}</div>;
};

export default FieldValueControl;
